/**
 * Cart drawer
 *
 * - Opens on click of any [data-cart-toggle] element (e.g. the header cart icon)
 * - Closes on backdrop click, [data-cart-close], or Escape
 * - Quantity +/- and Remove update via /cart/change.js
 * - Add/change use the Section Rendering API (sections param) so the drawer
 *   markup comes back in the same request — no extra /?sections or /cart.js calls.
 * - window.cartDrawer.addItem() adds to cart, opens the drawer with the new item
 *   on success, and shows the Shopify Ajax API error inside the drawer on failure.
 */
(() => {
  const SECTION_ID = 'cart-drawer';
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  let lastFocused = null;

  const isOpen = () => drawer.dataset.open === 'true';

  const clearError = () => {
    const el = drawer.querySelector('[data-cart-error]');
    if (el) {
      el.hidden = true;
      el.textContent = '';
    }
  };

  function open(keepError) {
    if (!keepError) clearError();
    // Commit the current (closed) styles so the slide-in always animates,
    // even when open() runs in the same task as an innerHTML swap.
    void drawer.offsetWidth;
    drawer.dataset.open = 'true';
    drawer.setAttribute('aria-hidden', 'false');
    const closeBtn = drawer.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  const close = () => {
    drawer.dataset.open = 'false';
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocused) lastFocused.focus();
  };

  const showError = (message) => {
    const el = drawer.querySelector('[data-cart-error]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    open(true);
  };

  const setLoading = (state) => {
    drawer.dataset.loading = state ? 'true' : 'false';
  };

  const syncCount = (count) => {
    if (count == null || Number.isNaN(count)) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  const render = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-cart-drawer]');
    if (!next) return;
    const wasOpen = isOpen();
    drawer.innerHTML = next.innerHTML;
    drawer.dataset.open = wasOpen ? 'true' : 'false';
    drawer.setAttribute('aria-hidden', wasOpen ? 'false' : 'true');
    syncCount(parseInt(next.dataset.itemCount, 10));
  };

  const refreshDrawer = async () => {
    const res = await fetch(`/?sections=${SECTION_ID}`);
    if (!res.ok) return;
    const data = await res.json();
    if (data[SECTION_ID]) render(data[SECTION_ID]);
  };

  const addItem = async (item) => {
    clearError();
    const items = Array.isArray(item) ? item : [item];
    // Single-item cart: clear whatever is already there so only the new selection remains.
    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: SECTION_ID }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const message = (data && data.description) || 'Could not add to cart.';
      showError(message);
      throw new Error(message);
    }
    if (data && data.sections && data.sections[SECTION_ID]) {
      render(data.sections[SECTION_ID]);
    } else {
      await refreshDrawer();
    }
    open();
    return data;
  };

  const changeLine = async (id, quantity) => {
    setLoading(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity, sections: SECTION_ID }),
      });
      if (!res.ok) throw new Error(`cart/change.js ${res.status}`);
      const cart = await res.json();
      if (cart.sections && cart.sections[SECTION_ID]) {
        render(cart.sections[SECTION_ID]);
      } else {
        await refreshDrawer();
      }
      syncCount(cart.item_count);
    } catch (err) {
      console.error('[cart-drawer]', err);
    } finally {
      setLoading(false);
    }
  };

  // Single delegated click handler — survives innerHTML swaps without rebinding
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle) {
      e.preventDefault();
      lastFocused = toggle;
      open();
      return;
    }

    if (!drawer.contains(e.target)) return;

    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      close();
      return;
    }

    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      const key = removeBtn.dataset.lineKey;
      if (!key) return; // sample line item
      changeLine(key, 0);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) close();
  });

  // Public API for custom add-to-cart buttons (e.g. the PDP buy box)
  window.cartDrawer = {
    open: () => open(),
    close,
    addItem,
    showError,
    refresh: () => refreshDrawer(),
  };
})();
